import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { crashState } from '../utils/crashHandler';
import FeedbackModal from '../components/FeedbackModal';

interface FeedbackContextType {
  showFeedback: () => void;
  hideFeedback: () => void;
}

const FeedbackContext = createContext<FeedbackContextType | undefined>(undefined);

export const FeedbackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [crashLog, setCrashLog] = useState(crashState.value.log);

  const showFeedback = useCallback(() => {
    setIsOpen(true);
  }, []);

  const hideFeedback = useCallback(() => {
    setIsOpen(false);
    crashState.next({ log: null, showFeedback: false });
  }, []);

  // Listen to crash state changes
  useEffect(() => {
    const subscription = crashState.subscribe(({ showFeedback, log }) => {
      if (showFeedback) {
        setIsOpen(true);
      }
      setCrashLog(log);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <FeedbackContext.Provider value={{ showFeedback, hideFeedback }}>
      {children}
      <FeedbackModal 
        isOpen={isOpen} 
        onClose={hideFeedback} 
        initialContent={crashLog ? `Error: ${crashLog.error.message}` : ''}
      />
    </FeedbackContext.Provider>
  );
};

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
}; 